import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';

import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';

import { bonusMessages } from 'lib/bonus';

import type {
  BonusBreakdownTooltip_powerBreakdown,
  BonusBreakdownTooltip_so5AppearanceBonusInterface,
} from './__generated__/index.graphql';

type Props = {
  bonuses?: BonusBreakdownTooltip_so5AppearanceBonusInterface[];
  powerBreakdown?: BonusBreakdownTooltip_powerBreakdown;
  captainBonus?: number | null;
  transferMalus?: number | null;
  children: ReactNode;
  skip?: boolean;
};

export const BonusBreakdownTooltip = ({
  bonuses,
  powerBreakdown,
  captainBonus,
  transferMalus,
  children,
  skip,
}: Props): ReactNode => {
  const { formatMessage } = useIntl();

  const normalizedBreakdownList = powerBreakdown
    ? [
        {
          message: formatMessage(bonusMessages.captain),
          value: captainBonus || 0,
        },
        {
          message: formatMessage(bonusMessages.levelBonus),
          value: powerBreakdown.xpBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.season),
          value: powerBreakdown.seasonBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.scarcity),
          value: powerBreakdown.scarcityBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.collection),
          value: transferMalus
            ? 0
            : powerBreakdown.collectionBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.club),
          value: powerBreakdown.activeClubsBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.nationality),
          value: powerBreakdown.nationalityBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.position),
          value: powerBreakdown.positionsBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.specialEdition),
          value: powerBreakdown.specialEditionCardBasisPoints / 10000,
        },
        {
          message: formatMessage(bonusMessages.transferMalus),
          value: transferMalus || 0,
          malus: true,
        },
      ].filter(Boolean)
    : bonuses?.map(({ title, bonusBasisPoints }) => ({
        message: title,
        value: bonusBasisPoints / 10000,
        malus: false,
      }));

  if (skip) {
    return children;
  }

  return (
    <Tooltip
      title={
        <Vertical>
          {normalizedBreakdownList
            ?.filter(({ value }) => value)
            .map(({ message, value, malus }, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <SBHorizontal key={i}>
                <LabelS bold color="var(--c-nd-500)">
                  {message}
                </LabelS>
                <Chip color={malus ? 'red' : 'green'}>
                  <FormattedNumber
                    value={value}
                    signDisplay="always"
                    style="percent"
                    maximumFractionDigits={1}
                  />
                </Chip>
              </SBHorizontal>
            ))}
        </Vertical>
      }
    >
      {children}
    </Tooltip>
  );
};

BonusBreakdownTooltip.fragments = {
  powerBreakdown: gql`
    fragment BonusBreakdownTooltip_powerBreakdown on PowerBreakdown {
      activeClubsBasisPoints
      captainBasisPoints
      collectionBasisPoints
      nationalityBasisPoints
      positionsBasisPoints
      scarcityBasisPoints
      seasonBasisPoints
      specialEditionCardBasisPoints
      xpBasisPoints
    }
  ` as TypedDocumentNode<BonusBreakdownTooltip_powerBreakdown>,
  so5AppearanceBonusInterface: gql`
    fragment BonusBreakdownTooltip_so5AppearanceBonusInterface on So5AppearanceBonusInterface {
      title
      bonusBasisPoints
    }
  ` as TypedDocumentNode<BonusBreakdownTooltip_so5AppearanceBonusInterface>,
};
